import { droneOf } from './drones';
import { ethers } from 'ethers';

/**
 * Format Utilities
 * 
 * Helper functions for formatting numbers, addresses, and values.
 * "Making big numbers look less scary since 2026." 📊
 */

/**
 * Format $FRAME wei amount to human-readable string
 * Handles the 18 decimal places of $FRAME
 * Set precise=true to show full number with commas instead of abbreviated
 */
export function formatFRAME(weiAmount: bigint | number | string, decimals = 2, precise = false): string {
  try {
    const wei = BigInt(weiAmount);
    const formatted = ethers.formatEther(wei);
    const num = parseFloat(formatted);
    
    // If precise mode, show full number with commas
    if (precise) {
      return num.toLocaleString('en-US', { 
        minimumFractionDigits: decimals, 
        maximumFractionDigits: decimals 
      });
    }
    
    // Format based on size (abbreviated)
    if (num >= 1_000_000_000_000_000) {
      return (num / 1_000_000_000_000_000).toFixed(decimals) + 'Q'; // Quadrillion
    } else if (num >= 1_000_000_000_000) {
      return (num / 1_000_000_000_000).toFixed(decimals) + 'T'; // Trillion
    } else if (num >= 1_000_000_000) {
      return (num / 1_000_000_000).toFixed(decimals) + 'B';
    } else if (num >= 1_000_000) {
      return (num / 1_000_000).toFixed(decimals) + 'M';
    } else if (num >= 1_000) {
      return (num / 1_000).toFixed(decimals) + 'K';
    } else if (num >= 1) {
      return num.toFixed(decimals);
    } else if (num > 0) {
      return num.toFixed(6);
    } else {
      return '0';
    }
  } catch (error) {
    return '0';
  }
}

/**
 * Format a plain number (not wei) to abbreviated FRAME display.
 * Rolls over at 999: 999M max → 1.00B, 999B max → 1.00T, etc.
 * Use this for share messages, tweets, Discord posts, and narratives.
 */
export function formatFRAMEShort(amount: number, decimals = 2): string {
  if (amount >= 1_000_000_000_000_000) {
    return (amount / 1_000_000_000_000_000).toFixed(decimals) + 'Q';
  } else if (amount >= 1_000_000_000_000) {
    return (amount / 1_000_000_000_000).toFixed(decimals) + 'T';
  } else if (amount >= 1_000_000_000) {
    return (amount / 1_000_000_000).toFixed(decimals) + 'B';
  } else if (amount >= 1_000_000) {
    return (amount / 1_000_000).toFixed(decimals) + 'M';
  } else if (amount >= 1_000) {
    return (amount / 1_000).toFixed(decimals) + 'K';
  } else if (amount >= 1) {
    return amount.toFixed(decimals);
  } else if (amount > 0) {
    return amount.toFixed(6);
  }
  return '0';
}

/**
 * Parse human-readable FRAME amount to wei
 */
export function parsePAW(amount: string | number): bigint {
  try {
    return ethers.parseEther(amount.toString());
  } catch (error) {
    return 0n;
  }
}

/**
 * Format address for display (shortened)
 */
export function formatAddress(address: string, prefixLength = 6, suffixLength = 4): string {
  if (!address || address.length < 10) return address;
  const d = droneOf(address);
  if (d) return `🤖 ${d.name}`;
  return `${address.slice(0, prefixLength)}...${address.slice(-suffixLength)}`;
}

/**
 * Format timestamp to relative time
 */
export function formatTimeAgo(timestamp: number | bigint): string {
  const now = Math.floor(Date.now() / 1000);
  const time = Number(timestamp);
  const diff = now - time;

  if (diff < 60) return 'just now';
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return `${Math.floor(diff / 86400)}d ago`;
}

/**
 * Format countdown duration
 */
export function formatCountdown(seconds: number | bigint): string {
  const secs = Number(seconds);
  
  if (secs <= 0) return '00:00:00';
  
  const hours = Math.floor(secs / 3600);
  const mins = Math.floor((secs % 3600) / 60);
  const s = secs % 60;
  
  return `${hours.toString().padStart(2, '0')}:${mins.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`;
}

/**
 * Format large number with commas
 */
export function formatNumber(num: number | bigint): string {
  return Number(num).toLocaleString();
}

/**
 * Format USD amount
 */
export function formatUSD(amount: number): string {
  if (amount >= 1_000_000_000) {
    return `$${(amount / 1_000_000_000).toFixed(2)}B`;
  } else if (amount >= 1_000_000) {
    return `$${(amount / 1_000_000).toFixed(2)}M`;
  } else if (amount >= 1_000) {
    return `$${(amount / 1_000).toFixed(2)}K`;
  } else if (amount >= 1) {
    return `$${amount.toFixed(2)}`;
  } else {
    return `$${amount.toFixed(4)}`;
  }
}

/**
 * Truncate text with ellipsis
 */
export function truncate(text: string, maxLength: number): string {
  if (text.length <= maxLength) return text;
  return text.slice(0, maxLength - 3) + '...';
}

/** Seed/placeholder wallets that must never render as live leaderboard or history. */
export function isDummyAddress(addr?: string | null): boolean {
  if (!addr) return true;
  const a = addr.toLowerCase();
  if (a === '0x0000000000000000000000000000000000000000' || a === 'none') return true;
  if (a.includes('deadbeef')) return true;
  if (/^0x0*1234/.test(a)) return true;
  if (a === '0x1234567890abcdef1234567890abcdef12345678') return true;
  return false;
}
